import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckCircle2, Layers3, List, Pencil, RefreshCw, UserRound } from "lucide-react";
import api from "@/lib/api";
import { fmtData } from "@/lib/helpers";
import { cycleFilterKey, cycleLabel, dailyTaskBuckets, groupByCycle, localDateKey, taskDisplayTitle } from "@/lib/dailyTasks";
import { TarefaEditDialog } from "@/components/TarefaEditDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import "./Hoje.css";

const PERIODS = [
  { key: "hoje", label: "Hoje", empty: "Nenhuma tarefa com prazo para hoje.", help: "As pendências anteriores continuam na aba Em atraso." },
  { key: "atraso", label: "Em atraso", empty: "Nenhuma tarefa em atraso.", help: "Os próximos prazos estão separados para você se organizar." },
  { key: "proximos", label: "Próximos dias", empty: "Nenhuma tarefa para os próximos 3 dias.", help: "As novas tarefas aparecerão aqui conforme os prazos se aproximarem." },
];
const VIEW_KEY = "netzer:hoje:group-by-cycle:v1";

function TaskRow({ task, today, onEdit }) {
  const overdue = task.prazo < today;
  return (
    <button type="button" className="daily-task" onClick={() => onEdit(task)} data-testid={`hoje-tarefa-${task.id}`}>
      <span className="daily-task-main">
        <span className="daily-task-title">{taskDisplayTitle(task)}</span>
        <span className="daily-task-context">
          <span className="daily-client">{task.cliente_empresa || "Estúdio"}</span>
          <span className="daily-cycle"><Layers3 size={13} aria-hidden="true" />{cycleLabel(task)}</span>
        </span>
      </span>
      <span className="daily-owner"><UserRound size={14} aria-hidden="true" /><span><span className="sr-only">Responsável: </span>{task.responsavel || "Não atribuído"}</span></span>
      <span className={`daily-deadline ${overdue ? "is-overdue" : ""}`}>
        <span>{overdue ? "Em atraso" : task.prazo === today ? "Para hoje" : "Prazo"}</span>
        <time dateTime={task.prazo}>{fmtData(task.prazo)}</time>
      </span>
      <span className="daily-edit" aria-hidden="true"><Pencil size={16} /></span>
      <span className="sr-only">Editar tarefa</span>
    </button>
  );
}

export default function Hoje({ embedded = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [equipe, setEquipe] = useState([]);
  const [tarefaEditando, setTarefaEditando] = useState(null);
  const [period, setPeriod] = useState("hoje");
  const [cycle, setCycle] = useState("all");
  const [limit, setLimit] = useState(8);
  const [grouped, setGrouped] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === "true"; } catch { return false; }
  });
  const load = useCallback(async () => {
    setLoading(true);
    try { const response = await api.get("/hoje"); setData(response.data); setError(false); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    api.get("/equipe").then((res) => setEquipe(res.data)).catch(() => {});
  }, [load]);

  if (!data) return <div className="daily-feedback" role={error ? "alert" : "status"}>
    <p>{error ? "Não foi possível carregar as tarefas." : "Carregando suas tarefas…"}</p>
    {error && <button type="button" onClick={load} disabled={loading}>Tentar novamente</button>}
  </div>;

  const today = data.hoje || localDateKey();
  const buckets = dailyTaskBuckets(data, today);
  const options = [...new Map(Object.values(buckets).flat().map((task) => [cycleFilterKey(task), cycleLabel(task)])).entries()]
    .sort(([a], [b]) => b.localeCompare(a));
  const filtered = Object.fromEntries(Object.entries(buckets).map(([key, tasks]) => [key, tasks.filter((t) => cycle === "all" || cycleFilterKey(t) === cycle)]));
  const tasks = filtered[period];
  const visible = tasks.slice(0, limit);
  const groups = grouped ? groupByCycle(visible) : [{ key: "list", tasks: visible }];
  const groupTotals = grouped ? new Map(groupByCycle(tasks).map((group) => [group.key, group.tasks.length])) : null;
  const recordings = data.gravacoes || [];
  const setGrouping = (value) => { setGrouped(value); try { localStorage.setItem(VIEW_KEY, String(value)); } catch { /* Optional preference storage. */ } };
  const validation = new Set([...(data.aguardando_aprovacao || []), ...(data.aguardando_cliente || [])].map((c) => c.id)).size + (data.publicacoes || []).length;

  return (
    <div className="daily-workspace" data-testid="hoje-page">
      <header className="daily-intro">
        <div>
          {!embedded && <h1>Hoje</h1>}
          <p className="daily-date">{new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long" }).format(new Date(`${today}T12:00:00`))}</p>
          <p className="daily-description">Uma tarefa por vez. Cada ciclo no seu lugar.</p>
        </div>
        <button type="button" className="daily-refresh" onClick={load} disabled={loading} aria-label="Atualizar tarefas"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /><span>Atualizar</span></button>
      </header>
      {error && <p role="alert" className="daily-error">Não foi possível atualizar. Os dados abaixo são da última consulta. <button onClick={load} disabled={loading}>Tentar novamente</button></p>}

      <div className="daily-layout">
        <section className="daily-panel" aria-label="Tarefas da equipe">
          {/* 21st.dev / Origin UI: scrollable tabs with counts, composed with existing Radix primitives. */}
          <Tabs value={period} onValueChange={(value) => { setPeriod(value); setLimit(8); }}>
            <TabsList className="daily-tabs" aria-label="Prazo das tarefas">
              {PERIODS.map((item) => <TabsTrigger key={item.key} value={item.key} className="daily-tab" data-testid={`hoje-tab-${item.key}`}>
                {item.label}<span className={`daily-tab-count ${item.key === "atraso" && filtered.atraso.length ? "has-overdue" : ""}`}>{filtered[item.key].length}</span>
              </TabsTrigger>)}
            </TabsList>
            <div className="daily-toolbar">
              <label className="daily-filter"><span>Ciclo</span><select aria-label="Filtrar por ciclo" value={cycle} onChange={(event) => { setCycle(event.target.value); setLimit(8); }}>
                <option value="all">Todos os ciclos</option>
                {options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                {cycle !== "all" && !options.some(([key]) => key === cycle) && <option value={cycle}>Ciclo sem pendências</option>}
              </select></label>
              <div className="daily-view" role="group" aria-label="Organização das tarefas">
                <button type="button" aria-pressed={!grouped} onClick={() => setGrouping(false)}><List size={16} aria-hidden="true" /><span>Lista</span></button>
                <button type="button" aria-pressed={grouped} onClick={() => setGrouping(true)}><Layers3 size={16} aria-hidden="true" /><span>Por ciclo</span></button>
              </div>
            </div>
            {PERIODS.map((item) => <TabsContent key={item.key} value={item.key} className="daily-content">
              <p className="sr-only" aria-live="polite">{tasks.length} tarefas nesta seleção</p>
              {tasks.length === 0 ? <div className="daily-empty"><CheckCircle2 size={28} aria-hidden="true" /><h2>{cycle === "all" ? item.empty : "Nenhuma tarefa neste ciclo e período."}</h2><p>{item.help}</p>{cycle !== "all" && <button type="button" onClick={() => setCycle("all")}>Ver todos os ciclos</button>}</div> :
                groups.map((group) => <div key={group.key} className="daily-task-group">
                  {grouped && <h2 className="daily-group-title"><span>{group.client}<span className="daily-group-cycle">{group.label}</span></span><span>{group.tasks.length < groupTotals.get(group.key) ? `${group.tasks.length} de ${groupTotals.get(group.key)}` : group.tasks.length} {groupTotals.get(group.key) === 1 ? "tarefa" : "tarefas"}</span></h2>}
                  {group.tasks.map((task) => <TaskRow key={task.id} task={task} today={today} onEdit={setTarefaEditando} />)}
                </div>)}
              {tasks.length > limit && <button className="daily-more" type="button" onClick={() => setLimit((value) => value + 8)}>Mostrar mais {Math.min(8, tasks.length - limit)} tarefas <span>({visible.length} de {tasks.length})</span></button>}
            </TabsContent>)}
          </Tabs>
          <p className="daily-hint">Clique em uma tarefa para editar ou concluir sem sair desta tela.</p>
        </section>

        <aside className="daily-aside">
          <section className="daily-panel daily-recordings" aria-label="Próximas gravações">
            <div className="daily-aside-heading"><CalendarDays size={18} aria-hidden="true" /><h2>Próximas gravações</h2></div>
            <p className="daily-aside-subtitle">Nos próximos 7 dias</p>
            {!recordings.length && <p className="daily-aside-empty">Nenhuma gravação agendada.</p>}
            {recordings.slice(0, 4).map((event) => <Link key={event.id} to="/producao?view=gravacoes" className="daily-recording">
              <span className="daily-recording-date"><time dateTime={event.data}>{fmtData(event.data).slice(0, 5)}</time><span>{event.hora_inicio || "Sem horário"}</span></span>
              <span><strong>{event.cliente_empresa || event.titulo}</strong><span>{event.cliente_empresa ? event.titulo : event.local}</span></span>
            </Link>)}
            <Link to="/producao?view=gravacoes" className="daily-aside-link">Ver gravações <ArrowRight size={15} /></Link>
          </section>
          <details className="daily-panel daily-summary">
            <summary>Resumo da operação</summary>
            <Link to="/producao?view=edicao">Em edição <span>{(data.em_edicao || []).length}</span></Link>
            <Link to="/producao?view=validacao">Validação e entrega <span>{validation}</span></Link>
            <Link to="/producao?view=pipeline">Bloqueados <span>{(data.bloqueados || []).length}</span></Link>
            <Link to="/producao?view=pipeline">Sem próxima ação <span>{(data.sem_acao || []).length}</span></Link>
          </details>
        </aside>
      </div>
      <TarefaEditDialog tarefa={tarefaEditando} equipe={equipe} onOpenChange={(open) => { if (!open) setTarefaEditando(null); }} onSaved={load} />
    </div>
  );
}
